import {Page} from "../page"

export const shopsPrices: Page = {
    uri: 'learn/shops-prices',
    icon: '🏷️',
    title: $localize`:@@page-shops-prices-title:Shops & Prices`,
    seoTitle: $localize`:@@page-shops-prices-seo-title:Shop price tracking — how comparisons and deal alerts work`,
    summary: $localize`:@@page-shops-prices-summary:Compare retail prices across shops on every game page, catch deals, and get notified of price drops.`,
    markdownContent: [
        $localize`:@@page-shops-prices-intro:Found a game you want? Ludoya shows you where to buy it. Every game page has a **Marketplace** section holding both second-hand copies from other players and new copies from retail shops, so you can compare the two without opening ten browser tabs.

![One game's store offers: shops with flags, prices converted to your currency](/assets/screenshots/shops-game-prices.webp)`,

        $localize`:@@page-shops-prices-tab:## The Marketplace Section

![A game's Marketplace section: a second-hand listing first, then store offers, with shops that can't ship to you folded away](/assets/screenshots/shops-marketplace-section.webp)

For each game you'll see:

- **Marketplace listings** — second-hand copies from players near you, listed first
- **Store offers** — one row per shop, grouped by availability: **Available**, **Preorder**, **Out of stock**
- **Prices in the shop's currency**, with an approximate conversion to yours next to it
- **The shop's own product title** — handy for telling editions and language versions apart
- **Country flags** so you know where each shop ships from

Offers from shops that don't ship to your country are folded away under their own toggle — visible if you're curious, but never cluttering the list of offers you can actually buy.

### The Price Chip and Price History

At the top of every game page, a chip shows the lowest price you can get it for, in a shop or on the marketplace. Tap it for every offer on one page, and a **Price history** chart: the lowest price each day at shops that ship to you, so you can tell a real deal from the usual price.`,

        $localize`:@@page-shops-prices-deals:## Deals & Promo Codes

When a shop runs a discount, the offer shows a **"% off" tag** with the previous price crossed out. Some shops also offer Ludoya users a **promo code** — the listed price already reflects the discount, and Ludoya shows you the code to apply at checkout so you actually get it.`,

        $localize`:@@page-shops-prices-shops-on-ludoya:## Shops on Ludoya

Some shops are more than a price source — they run their own Ludoya [organization account](/learn/organizations). Their offers link to their profile, where you can follow them, browse their events, and see their activity in the community.`,

        $localize`:@@page-shops-prices-notifications:## Never Miss a Deal

Shop deals and new arrivals also appear as posts in your [feed](/learn/social-feeds), and you can get notified directly: turn on **Price drops** and **New arrivals** under Settings → [Notifications](/learn/notifications), and pick the channel — push, email, or in-app. Both are about games on your [wishlist](/learn/collection), at shops that reach you.

![New-arrival posts from shops in the feed](/assets/screenshots/shops-feed-deals.webp)`,

        $localize`:@@page-shops-prices-offer-page:## The Offer Page

Tap a deal in your feed and it opens as its own page, with the game, the shop, the price, and the previous price struck through next to a **% off** badge — or a **New** flag if it's a fresh arrival. **View at shop** takes you to the retailer to buy.

Offers are ordinary posts underneath, so you can **like** one and **comment** on it. That turns a good price into something your group actually notices: someone spots a deal, the people who follow them see it, and the copy gets bought before it sells out.`,

        $localize`:@@page-shops-prices-related:## Related

- [Marketplace](/learn/marketplace) — buy, sell, and trade second-hand with other players
- [Collection](/learn/collection) — track what you own and what you wish for
- [Notifications](/learn/notifications) — deal alerts, your way
- [Organizations](/learn/organizations) — how shops live on Ludoya`,
    ].join('\n\n---\n\n')
}
